import { EIMZOClient as client } from "./e-imzo-client";

const CAPIWS = window.CAPIWS;

/**
 * Плагин хранит ключи плоским списком: домен, ключ, домен, ключ...
 * Списки объединяются по домену, чтобы ключи вызывающей стороны не
 * вытесняли то, что уже объявлено в e-imzo-client.js.
 *
 * @param {string[]} base
 * @param {string[]} extra
 * @return {string[]}
 */
function mergeApiKeys(base, extra) {
  const merged = [...base];

  for (let i = 0; i + 1 < extra.length; i += 2) {
    const domain = extra[i];
    let exists = false;

    for (let j = 0; j < merged.length; j += 2) {
      if (merged[j] === domain) {
        exists = true;
        break;
      }
    }

    if (!exists) merged.push(domain, extra[i + 1]);
  }

  return merged;
}

/**
 * @typedef Cert
 * @property {string} disk
 * @property {string} path
 * @property {string} name
 * @property {string} alias
 * @property {string} serialNumber
 * @property {string} validFrom
 * @property {string} validTo
 * @property {string} CN
 * @property {string} TIN
 * @property {string} UID
 * @property {string} O
 * @property {string} T
 * @property {string} type
 */

/**
 * @typedef SignPkcs7Result
 * @property {string} pkcs7_64
 * @property {string} signature_hex
 * @property {string} signer_serial_number
 */

/**
 * @typedef LoadKeyResult
 * @property {string} id
 * @property {Cert} cert
 **/

export default class EIMZO {
  /**
   * @type {?Cert}
   */
  _loadedKey = null;

  get loadedKey() {
    return this._loadedKey;
  }

  set loadedKey(value) {
    this._loadedKey = value;
  }

  apiKeys = [
    "localhost",
    "96D0C1491615C82B9A54D9989779DF825B690748224C2B04F500F370D51827CE2644D8D4A82C18184D73AB8530BB8ED537269603F61DB0D03D2104ABF789970B",
    "127.0.0.1",
    "A7BCFA5D490B351BE0754130DF03A068F855DB4333D43921125B9CF2670EF6A40370C646B90401955E1F7BC9CDBF59CE0B2C5467D820BE189C845D0B79CFC96F",
  ];

  /**
   * @return {Promise<{major: Number, minor: Number}>}
   */
  async checkVersion() {
    return new Promise((resolve, reject) => {
      client.checkVersion(
        function (major, minor) {
          resolve({ major, minor });
        },
        function (error, message) {
          reject(error, message);
        }
      );
    });
  }

  async installApiKeys() {
    /* Ключи стендов объявлены в e-imzo-client.js, здесь они только
       дополняются: раньше install() перезаписывал список клиента, и на
       dev-afp.agrobank.uz в плагин уходили одни localhost и 127.0.0.1 —
       apikey() отвечал отказом, и вход по сертификату не работал. */
    client.API_KEYS = mergeApiKeys(client.API_KEYS, this.apiKeys);

    return new Promise((resolve, reject) => {
      client.installApiKeys(resolve, reject);
    });
  }

  /**
   * @return {Promise<Cert[]>}
   */
  async listAllUserKeys() {
    return new Promise((resolve, reject) => {
      client.listAllUserKeys(
        function (cert, index) {
          return "cert-" + cert.serialNumber + "-" + index;
        },
        function (index, cert) {
          return cert;
        },
        function (items, firstId) {
          resolve(items, firstId);
        },
        function (error, reason) {
          reject(error, reason);
        }
      );
    });
  }

  /**
   * @param {Cert} cert
   * @return {Promise<LoadKeyResult>}
   */
  async loadKey(cert) {
    /* Клиент молча игнорирует пустой и неизвестный ключ — не зовёт ни
       success, ни fail. Промис при этом не завершается никогда, и
       подписание зависало на спиннере, когда сертификат не найден. */
    if (!cert) {
      return Promise.reject(new Error("E-IMZO: сертификат не выбран"));
    }

    if (cert.type !== "pfx" && cert.type !== "ftjc") {
      return Promise.reject(
        new Error("E-IMZO: неподдерживаемый тип ключа - " + cert.type)
      );
    }

    return new Promise((resolve, reject) => {
      client.loadKey(
        cert,
        (id) => {
          this._loadedKey = cert;
          resolve({ cert, id });
        },
        reject,
        true
      );
    });
  }

  /**
   * @param {string} loadKeyId
   * @return {Promise<string[]>}
   */
  async getCertificateChain(loadKeyId) {
    return new Promise((resolve, reject) => {
      CAPIWS.callFunction(
        {
          plugin: "x509",
          name: "get_certificate_chain",
          arguments: [loadKeyId],
        },
        (event, data) => {
          if (data.success) {
            resolve(data.certificates);
          } else {
            reject("Failed");
          }
        },
        reject
      );
    });
  }

  /**
   * @param {string} loadKeyId
   * @return {Promise<?string>}
   */
  async getMainCertificate(loadKeyId) {
    let result = await this.getCertificateChain(loadKeyId);

    if (Array.isArray(result) && result.length > 0) {
      return result[0];
    }
    return null;
  }

  /**
   * @param {string} cert
   * @return {Promise<void>}
   */
  async getCertInfo(cert) {
    return new Promise((resolve, reject) => {
      CAPIWS.callFunction(
        { name: "get_certificate_info", arguments: [cert] },
        (event, data) => {
          if (data.success) {
            resolve(data.certificate_info);
          } else {
            reject("Failed");
          }
        },
        reject
      );
    });
  }

  /**
   * @param {Cert} cert
   * @param {string} content
   * @return {Promise<SignPkcs7Result>}
   */
  async signPkcs7(cert, content) {
    let loadKeyResult = await this.loadKey(cert);

    return new Promise((resolve, reject) => {
      CAPIWS.callFunction(
        {
          name: "create_pkcs7",
          plugin: "pkcs7",
          arguments: [window.Base64.encode(content), loadKeyResult.id, "no"],
        },
        (event, data) => {
          if (data.success) {
            resolve(data);
          } else {
            reject("Failed");
          }
        },
        reject
      );
    });
  }

  /**
   * @param {string} id of loaded cert
   * @param {string} content
   * @param {?Function} timestamper - function to get timestamp data from server
   * @return {Promise<SignPkcs7Result>}
   */
  async createPkcs7(id, content, timestamper) {
    return new Promise((resolve, reject) => {
      client.createPkcs7(
        id,
        content,
        timestamper,
        (/* string */ pkcs7) => {
          resolve(pkcs7);
        },
        /* Клиент зовёт fail(error, reason): причина отказа плагина приходит
           вторым аргументом, а reject забирал только первый — в консоли
           оставался голый null вместо текста ошибки E-IMZO. */
        (error, reason) => {
          if (error instanceof Error) reject(error);
          else reject(new Error(reason || error || "E-IMZO: номаълум хато"));
        }
      );
    });
  }

  /**
   * @param {string} signature
   *
   * @return {Promise<string>}
   */
  async getTimestampToken(signature) {
    return new Promise((resolve, reject) => {
      CAPIWS.callFunction(
        {
          name: "get_timestamp_token_request_for_signature",
          arguments: [signature],
        },
        function (event, data) {
          if (data.success) {
            resolve(data.timestamp_request_64);
          } else {
            reject("Failed");
          }
        },
        reject
      );
    });
  }

  /**
   * @param {string} domain
   * @param {string} key
   */
  addApiKey(domain, key) {
    if (!this.apiKeys.includes(domain)) {
      this.apiKeys.push(domain, key);
    }
  }

  async install() {
    await this.checkVersion();

    await this.installApiKeys();
  }
}
